import { defineNuxtPlugin, useNuxtApp } from 'nuxt/app';
import { useAwAnalyticsStore } from '~~/common/stores/awAnalytics';
import type { DataLayerItem } from '~~/common/types/global';

const AUCHAN_GTM_0 = 'AUCHAN_GTM_0';

function createPluginProvides () {
  const nuxtApp = useNuxtApp();
  const {
    $gtmLogger,
    $pinia,
    $awMergedConfig,
  } = nuxtApp;
  const awAnalyticsStore = useAwAnalyticsStore($pinia);

  class G {
    private _isTrackingConfigValid: boolean;
    private _getSharedData: () => DataLayerItem[];
    private _pushSharedData: (data: { value: DataLayerItem }) => void;
    private _isSharedDataPushed: boolean;

    /* getSharedData is good for persisting data between SSR and browser */
    constructor ({
      getSharedData,
      pushSharedData,
    }: {
      getSharedData: () => DataLayerItem[];
      pushSharedData: (data: { value: DataLayerItem }) => void;
    }) {
      this._isTrackingConfigValid = Boolean($awMergedConfig.googleTagManagerId);
      this._getSharedData = getSharedData;
      this._pushSharedData = pushSharedData;
      this._isSharedDataPushed = false;
    }

    push (...data: DataLayerItem[]): void {
      try {
        if (this._isTrackingConfigValid) {
          if (typeof window === 'undefined') {
            data.forEach((d) => {
              $gtmLogger.info('pushPre |', d);
              this._pushSharedData({ value: d });
            });
          } else {
            this.serializeSharedData();
            data.forEach((d) => {
              $gtmLogger.info('pushPost |', d);
              window.dataLayer.push(d);
            });
          }
        }
      } catch (err) {
        $gtmLogger.error(err);
      }
    }

    serializeSharedData (): void {
      if (!this._isSharedDataPushed && typeof window !== 'undefined') {
        this._isSharedDataPushed = true;
        $gtmLogger.info('initializedData', this._getSharedData());
        const dataCopy: DataLayerItem[] = JSON.parse(JSON.stringify(this._getSharedData()));
        if (window.dataLayer) {
          dataCopy.forEach((d: DataLayerItem) => window.dataLayer.push(d));
        } else {
          window.dataLayer = dataCopy;
        }
      }
    }
  }

  const awGtm = new G({
    getSharedData (): DataLayerItem[] {
      return awAnalyticsStore[AUCHAN_GTM_0] ?? [];
    },
    pushSharedData ({ value }: { value: DataLayerItem }): void {
      awAnalyticsStore.pushToAnalytics({
        id: AUCHAN_GTM_0,
        value,
      });
    },
  });

  awGtm.serializeSharedData();

  if (!awAnalyticsStore[AUCHAN_GTM_0]) {
    awAnalyticsStore.setAnalytics({
      id: AUCHAN_GTM_0,
      value: [],
    });
  }

  return {
    awGtm,
  };
}

export type AwGtmPluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});
